import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import LeadNurture from './LeadNurture';
import TodoList from './TodoList';
import { Workflow, ListChecks } from 'lucide-react';

/**
 * Work — unified workspace that merges Lead Nurture and the To-Do List.
 *
 * Two top-of-page views:
 *   • Pipeline (Kanban) — the existing Lead Nurture page, rendered in
 *     embedded mode so it doesn't draw its own Header.
 *   • Tasks (list)      — the existing To-Do List, also embedded. Its data
 *     source (/api/todos) now aggregates personal todos + lead-linked
 *     follow-ups + Hey Derwent reminders, so this view is the single source of
 *     truth for "what's on my plate".
 *
 * URL: /work?view=pipeline|tasks (default: pipeline).
 * Legacy /todos and /lead-nurture URLs redirect here.
 */
const Work = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse view from query string. Default to "tasks" — the unified To-Do
  // List is the single feed of personal todos + lead follow-ups + Hey Derwent
  // reminders, so it's the natural "what should I do today" landing.
  const params = new URLSearchParams(location.search);
  const initial = params.get('view') === 'pipeline' ? 'pipeline' : 'tasks';
  const [view, setView] = useState(initial);

  // Keep the URL in sync when the user flips tabs so the back button works.
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    const cur = next.get('view') === 'pipeline' ? 'pipeline' : 'tasks';
    if (cur !== view) {
      next.set('view', view);
      navigate(`/work?${next.toString()}`, { replace: true });
    }
  }, [view, location.search, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-page text-slate-900 dark:text-slate-100">
      <Header />

      {/* View switcher — sits above the embedded page so neither component has
          to know about the other. */}
      <div className="border-b border-slate-200/70 dark:border-slate-800 bg-transparent">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 mr-2 hidden sm:inline">
            Workspace
          </span>
          <div className="inline-flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full p-1 shadow-sm" role="tablist">
            {[
              { k: 'tasks',    l: 'Tasks',    Icon: ListChecks },
              { k: 'pipeline', l: 'Pipeline', Icon: Workflow },
            ].map(({ k, l, Icon }) => (
              <button
                key={k}
                type="button"
                onClick={() => setView(k)}
                data-testid={`work-tab-${k}`}
                aria-pressed={view === k}
                className={
                  'inline-flex items-center gap-1.5 px-4 sm:px-5 py-1.5 rounded-full text-[13px] font-semibold transition-all ' +
                  (view === k
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white')
                }
              >
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 ml-auto hidden md:block">
            {view === 'pipeline'
              ? 'Your Kanban + lead drawer. Follow-ups here appear in Tasks too.'
              : 'Personal todos + lead follow-ups + Hey Derwent reminders, in one feed.'}
          </p>
        </div>
      </div>

      {/* Embedded page body */}
      <div className="flex-1 flex flex-col" data-testid={`work-view-${view}`}>
        {view === 'pipeline'
          ? <LeadNurture embedded />
          : <TodoList embedded />}
      </div>
    </div>
  );
};

export default Work;
